import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, Theme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { SaleDepositSlip } from "../../types/sale-deposit.type";

interface SlipFullScreenViewerProps {
  /** Index of the slip to open; null = closed. */
  index: number | null;
  slips: SaleDepositSlip[];
  onChangeIndex: (index: number) => void;
  onClose: () => void;
}

/** Scrim opacity chosen so a white bank slip carries the whole visual weight. */
const SCRIM_OPACITY = 0.94;
const CONTROL_OPACITY = 0.12;
const CONTROL_HOVER_OPACITY = 0.24;
const PLACEHOLDER_OPACITY = 0.7;

const navigationButtonSx = (edge: "left" | "right") => ({
  position: "absolute" as const,
  [edge]: 8,
  color: "common.white",
  bgcolor: (theme: Theme) => alpha(theme.palette.common.white, CONTROL_OPACITY),
  "&:hover": {
    bgcolor: (theme: Theme) =>
      alpha(theme.palette.common.white, CONTROL_HOVER_OPACITY),
  },
});

/**
 * Full-screen slip inspection. The list modal caps each image inside a small dialog, which
 * shrinks a portrait bank slip below readable size; this fills the viewport instead, still
 * `objectFit: contain` so nothing is cropped.
 *
 * The image `src` is the presigned URL that already came down with the row, so opening costs
 * no request. Two consequences are handled here:
 *  - the URL is signed for 24h, so a page left open overnight can 403 — hence the same
 *    onError fallback the list modal has, rather than a blank black screen;
 *  - the parent refetches the list after a slip-amount correction, which mints a *different*
 *    signed URL for the same image. Error state is keyed on the slip uuid (stable) rather than
 *    the url (churns), so a refetch does not resurrect a stale "broken" state.
 *
 * Rendered as a sibling Dialog on top of TransferSlipsModal; MUI stacks it and ESC closes only
 * the topmost one, so the user lands back on the slip list rather than losing both.
 */
export default function SlipFullScreenViewer({
  index,
  slips,
  onChangeIndex,
  onClose,
}: SlipFullScreenViewerProps) {
  const [failedSlipUuids, setFailedSlipUuids] = useState<Record<string, boolean>>(
    {},
  );
  const isOpen = index !== null;
  const slip = isOpen ? (slips[index] ?? null) : null;
  const hasPrevious = isOpen && index > 0;
  const hasNext = isOpen && index < slips.length - 1;
  const slipLabel = `สลิป ${slip?.sequence || (index ?? 0) + 1}`;

  // A fresh presign after a refetch deserves a fresh attempt at loading the image.
  useEffect(() => {
    if (!isOpen) setFailedSlipUuids({});
  }, [isOpen]);

  const showPrevious = useCallback(() => {
    if (index !== null && index > 0) onChangeIndex(index - 1);
  }, [index, onChangeIndex]);

  const showNext = useCallback(() => {
    if (index !== null && index < slips.length - 1) onChangeIndex(index + 1);
  }, [index, slips.length, onChangeIndex]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleArrowKeys = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") showPrevious();
      if (event.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handleArrowKeys);
    return () => window.removeEventListener("keydown", handleArrowKeys);
  }, [isOpen, showPrevious, showNext]);

  const markImageFailed = useCallback(() => {
    if (slip) setFailedSlipUuids((previous) => ({ ...previous, [slip.uuid]: true }));
  }, [slip]);

  const isImageUnavailable =
    !slip?.slip_url || Boolean(slip && failedSlipUuids[slip.uuid]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen
      slotProps={{
        paper: {
          sx: {
            bgcolor: (theme) => alpha(theme.palette.common.black, SCRIM_OPACITY),
          },
        },
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ position: "relative", width: "100%", height: "100%" }}
      >
        <Typography
          sx={{
            position: "absolute",
            top: 16,
            left: 20,
            color: "common.white",
            fontWeight: 700,
          }}
        >
          {slipLabel}
          {slips.length > 1 ? ` / ${slips.length}` : ""}
        </Typography>

        <IconButton
          aria-label="ปิด"
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "common.white" }}
        >
          <CloseIcon />
        </IconButton>

        {hasPrevious && (
          <IconButton
            aria-label="สลิปก่อนหน้า"
            onClick={showPrevious}
            sx={navigationButtonSx("left")}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        {hasNext && (
          <IconButton
            aria-label="สลิปถัดไป"
            onClick={showNext}
            sx={navigationButtonSx("right")}
          >
            <ChevronRightIcon />
          </IconButton>
        )}

        {isImageUnavailable ? (
          <Stack
            alignItems="center"
            gap={1}
            sx={{
              color: (theme) =>
                alpha(theme.palette.common.white, PLACEHOLDER_OPACITY),
            }}
          >
            <ImageNotSupportedOutlinedIcon sx={{ fontSize: 56 }} />
            <Typography variant="body2">
              {slip?.slip_url ? "ไม่สามารถแสดงรูปสลิปได้" : "ไม่มีรูปสลิป"}
            </Typography>
          </Stack>
        ) : (
          <Box
            component="img"
            src={slip?.slip_url ?? undefined}
            alt={slipLabel}
            onError={markImageFailed}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        )}
      </Stack>
    </Dialog>
  );
}
